import { createContext, useContext, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePortfolioAuth } from "@/hooks/usePortfolioAuth";
import Icon from "@/components/base/Icon";
import ViewSwitch from "@/components/feature/ViewSwitch";
import SiteTop from "@/components/feature/SiteTop";

/* NDA case studies. With a `summary`, people without the password read the
   short public version, and a switch at the top lets anyone move between
   the short and the full version (the full one asks for the password).
   With the password the full version opens first. "?view=short" keeps the
   short version in the address, so it can be shared.

   The route wraps each NDA page in a gate with a `summary`, and the page
   itself has a plain gate inside. Only the outer one puts the header of the
   site on the page: the inner one knows it is inside another gate and adds
   nothing, so the header is there once. */
const InsideGate = createContext(false);

export default function ProtectedPageGate({ children, summary }: { children: ReactNode; summary?: ReactNode }) {
  const { isAuthorized, openModal } = usePortfolioAuth();
  const [params, setParams] = useSearchParams();
  const insideGate = useContext(InsideGate);

  if (summary) {
    const view = !isAuthorized || params.get("view") === "short" ? "short" : "full";
    const choose = (next: "short" | "full") => {
      if (next === "full" && !isAuthorized) return openModal();
      setParams(next === "short" ? { view: "short" } : {}, { replace: true });
    };
    return (
      <InsideGate.Provider value={true}>
        <div className="relative with-site-nav">
          <SiteTop />
          <ViewSwitch
            view={view}
            locked={!isAuthorized}
            hints={isAuthorized ? undefined : { short: "You are reading the short version. Full shows the screens and needs a password." }}
            onChange={choose}
          />
          {view === "short" ? summary : children}
        </div>
      </InsideGate.Provider>
    );
  }

  if (isAuthorized) {
    if (insideGate) return <>{children}</>;
    return (
      <div className="relative with-site-nav">
        <SiteTop />
        {children}
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-950 flex items-center justify-center px-4"
      style={{ fontFamily: "'Ronzino', system-ui, sans-serif" }}
    >
      <div className="w-full max-w-md text-center">
        <div className="bg-gray-900/70 border border-gray-800 rounded-lg p-8">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5 rounded-full bg-gray-800">
            <Icon name="lock" className="text-gray-300 text-2xl" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-light text-white mb-3">
            This case study is password protected
          </h1>
          <p className="text-gray-300 text-base leading-relaxed mb-6">
            One password unlocks all four SkillLab case studies, so you only need to enter it once.
          </p>

          <button
            type="button"
            onClick={openModal}
            className="btn btn-primary w-full py-3 text-base whitespace-nowrap"
          >
            Enter password
          </button>

          <div className="mt-5 pt-4 border-t border-gray-800">
            <Link
              to="/"
              className="btn btn-ghost -ml-3 text-base"
            >
              <Icon name="arrow-left" aria-hidden="true" />
              Back to Portfolio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
